import { Component, OnInit } from '@angular/core';
import { Sidenavaction } from '../../../../../../assets/js/application/sidenavaction';
import { SyncService } from 'src/app/services/sync.service';
import SyncModel from 'src/app/services/sync.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

/**
 * AclSyncComponent
 *
 * This component is responsible for managing the synchronization of Access Control List (ACL)
 * permissions and roles across the application. It provides an administrative interface
 * to manually trigger the synchronization process, ensuring that user permissions are
 * up-to-date with the latest policy definitions and role assignments.
 *
 * Key Responsibilities:
 * - Trigger manual synchronization of ACL details via SyncService
 * - Manage the synchronization state and data model
 * - Provide user feedback upon successful synchronization (alerts)
 * - Handle errors during the synchronization process using CommonMethodsService
 * - Track navigation history for proper routing behavior
 *
 * User Roles:
 * - System Administrators: Typically restricted to high-level admins who manage system configurations.
 *
 * Features:
 * - Manual Sync Trigger: Allows admins to force an update of ACLs.
 * - Error Handling: Catches and displays user-friendly error messages.
 * - Navigation Tracking: Records the current route before performing actions.
 *
 * Data Flow:
 * 1. User initiates sync action (usually via button click invoking aclSync()).
 * 2. Component records current route in PreviousRouteService.
 * 3. Component initializes a new SyncModel.
 * 4. Component calls SyncService.syncACLDetails() to perform the backend synchronization.
 * 5. On success, an alert confirms the update.
 * 6. On failure, CommonMethodsService displays an error message.
 *
 * @component
 * @selector app-acl-sync
 * @example
 * <app-acl-sync></app-acl-sync>
 *
 * @implements {OnInit}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-acl-sync',
  templateUrl: './acl-sync.component.html',
  styleUrls: ['./acl-sync.component.scss']
})
export class AclSyncComponent implements OnInit {
  /**
   * Model to hold synchronization data and results
   * @type {SyncModel}
   */
  public sync: SyncModel;

  /**
   * Constructor for AclSyncComponent
   *
   * Initializes the component and injects necessary services for synchronization,
   * navigation, and error handling. Also initializes the sync model.
   *
   * @constructor
   * @param {SyncService} syncService - Service responsible for ACL synchronization API calls
   * @service SyncService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions (if used in template)
   * @service Sidenavaction
   * @param {CommonMethodsService} commonMethodsService - Service for common utilities and error handling
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service to interact with the browser's URL
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service to track navigation history
   * @service PreviousRouteService
   */
  constructor(public syncService: SyncService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.sync = new SyncModel();
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Called once after the component is initialized.
   * Currently empty as no specific initialization logic is required for this component
   * beyond the constructor setup.
   *
   * @method ngOnInit
   * @lifecycle Angular component initialization hook
   * @returns {void}
   */
  public ngOnInit() {
  }

  /**
   * Triggers the ACL synchronization process
   *
   * This asynchronous method performs the following steps:
   * 1. Records the current route path using PreviousRouteService.
   * 2. Creates a new SyncModel instance as input.
   * 3. Calls the SyncService to synchronize ACL details with the backend.
   * 4. Updates the local sync property with the result.
   * 5. Displays a success alert to the user.
   *
   * Error Handling:
   * - Catches any errors during the async operation.
   * - Delegates error display to CommonMethodsService.userFriendlyAlertMessages.
   *
   * @async
   * @method aclSync
   * @returns {Promise<void>}
   * @throws {Error} If the synchronization API call fails
   * @side-effect Updates 'sync' property, shows browser alert
   *
   * @example
   * // Called from template button click
   * <button (click)="aclSync()">Sync ACL</button>
   */
  public async aclSync() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      let inputData = new SyncModel();
      this.sync = await this.syncService.syncACLDetails(inputData);
      alert("Updated ACL Details to users.");
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
